/* eslint-disable react/no-children-prop */
'use client';

import Flag from '@app/_components/Flag';
import { BaseSelect } from '@app/_components/inputs/Select';
import LinkOutlined from '@app/_components/LinkOutlined';
import { useMarket } from '@app/_context';
import { Button, Input } from '@ngg/components';
import { CheckIcon } from '@ngg/icons';
import type { Form, KeyValue, Maybe } from '@server/gql/graphql';
import { registerUser } from '@server/requests/user';
import { type AnyFieldApi, useForm } from '@tanstack/react-form';
import { setCookie } from 'cookies-next';
import { useRouter } from 'next/navigation';
import type { PropsWithChildren } from 'react';
import type { Dictionary } from 'types';
import { z } from 'zod';

import { BRAND_CODE } from '@/config/common';
import { marketConfigurations } from '@/config/market-configurations';
import type { MarketConfiguration, MarketEnum } from '@/config/markets/const';
import { cn } from '@/lib/utils';
import { createDictionary } from '@/utils/common';

import { NewCustomerHeader } from './newCustomerHeader';

function FieldInfo({
    field,
    dictionary,
}: {
    field: AnyFieldApi;
    dictionary: Dictionary;
}) {
    if (!field.state.meta.isTouched || field.state.meta.isValid) return null;
    const [error] = field.state.meta.errors;
    return (
        <span className="text-sm text-error">{dictionary[error.message]}</span>
    );
}

type FlatMarketConfig = {
    id: string;
    value: string;
    label: string;
};

function getFlatMarketConfig(market: MarketConfiguration) {
    return {
        id: market.code,
        value: market.code,
        label: market.label,
    };
}

const flatMarketConfigs: FlatMarketConfig[] = marketConfigurations
    .filter((m) => m.code !== 'se' && m.code !== 'no')
    .map(getFlatMarketConfig);

const SelectIcon = ({ id }: { id: MarketEnum }) => (
    <Flag code={id} className="h-full w-full rounded-full object-cover" />
);

export default function NewCustomerForm({
    dictionary,
    children,
    classNames,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onClose,
}: PropsWithChildren<{
    dictionary: Dictionary;
    onClose?: () => void;
    classNames?: {
        instagramButton?: string;
        emailInput?: string;
        submitButton?: string;
        selectWrapper?: string;
        selectOption?: string;
        wrapper?: string;
    };
}>) {
    const router = useRouter();
    const { market } = useMarket().state;
    const showCountrySelect = market.code !== 'no' && market.code !== 'se';

    const form = useForm({
        defaultValues: {
            email: '',
            countryCode:
                flatMarketConfigs.find((m) => m.id === market.code) ??
                getFlatMarketConfig(market),
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                try {
                    await registerUser({
                        brandCode: BRAND_CODE,
                        contact: {
                            email: value.email,
                            countryCode: value.countryCode?.id ?? market.code,
                            externalStoreId: market.externalStoreId,
                            source: 'new-form',
                        },
                    });
                    setCookie('ng_newsletter', true, {
                        maxAge: 60 * 60 * 24 * 7,
                    });
                } catch (err) {
                    if (err instanceof Error && err.message === 'Conflict') {
                        return 'Already registered!';
                    }
                    return 'Please try again later';
                }
            },
        },
        onSubmit: () => null,
    });

    if (form.state.isSubmitSuccessful) {
        return (
            <div>
                <h2 className="text-lg font-bold">
                    {dictionary?.newCustomerFormSuccessHeading}
                </h2>
                <p className="mt-4 text-sm">
                    {dictionary?.newCustomerFormSuccessText}
                </p>
                <Button
                    onClick={() => router.push('/')}
                    fullWidth
                    className="btn-outlined mt-4 bg-white text-black hover:border-white">
                    {dictionary?.newCustomerFormSuccessButtonText}
                </Button>
            </div>
        );
    }

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
            }}
            className={cn(
                'grid w-full grid-cols-1 gap-6 md:gap-12',
                classNames?.wrapper,
            )}>
            {children}
            <div className={cn('flex w-full max-w-full flex-col')}>
                <label className="mb-2 block text-sm font-bold">
                    1. {dictionary?.newCustomerFormInstagramButtonLabel}
                </label>
                <LinkOutlined
                    href={
                        dictionary?.newCustomerFormInstagramUrl ||
                        'https://www.instagram.com/nordiskagalleriet/'
                    }
                    className={cn(
                        'group',
                        'bg-black text-white',
                        'visited:border-[#25C50B] visited:bg-[#25C50B] hover:visited:border-black',
                    )}
                    target="_blank"
                    startIcon={
                        <span
                            className={cn(
                                'flex items-center',
                                '-ml-[14px] rounded-full',
                                'bg-black group-visited:bg-white group-hover:bg-white',
                            )}>
                            <CheckIcon
                                className={cn(
                                    'pt-[1px]',
                                    'text-black',
                                    'group-hover:text-white',
                                    'group-visited:text-black group-hover:group-visited:text-black',
                                )}
                            />
                        </span>
                    }>
                    {dictionary?.newCustomerFormInstagramButtonText}
                </LinkOutlined>
                <div className="py-4 space-y-4">
                    <form.Field
                        name="email"
                        validators={{
                            onSubmit: z
                                .string()
                                .email({ message: 'errorEmailInvalid' }),
                        }}
                        children={(field) => (
                            <>
                                <Input
                                    id={field.name}
                                    type="email"
                                    name={field.name}
                                    placeholder={
                                        dictionary?.newCustomerFormEmailInputPlaceholder
                                    }
                                    value={field.state.value}
                                    required
                                    fullWidth
                                    label={`2. ${dictionary?.newCustomerFormEmailInputLabel}`}
                                    onChange={(e) =>
                                        field.handleChange(e.target.value)
                                    }
                                    classNames={{
                                        label: 'text-sm font-bold mb-2',
                                        input: cn(
                                            'h-10 w-full border-grey-150 px-4 py-0 text-base placeholder:text-sm',
                                            classNames?.emailInput,
                                        ),
                                    }}
                                />
                                <FieldInfo
                                    field={field}
                                    dictionary={dictionary}
                                />
                            </>
                        )}
                    />
                    {showCountrySelect && (
                        <form.Field
                            name="countryCode"
                            children={(field) => (
                                <BaseSelect
                                    id={field.name}
                                    name={field.name}
                                    required
                                    placeholder={
                                        dictionary?.newCustomerFormCountrySelectPlaceholder
                                    }
                                    options={flatMarketConfigs}
                                    onChange={(e) =>
                                        field.handleChange(
                                            e as FlatMarketConfig,
                                        )
                                    }
                                    value={field.state.value}
                                    optionIcon={SelectIcon}
                                    labelIcon={SelectIcon}
                                    classNames={{
                                        button: 'border-beige-150',
                                        dropdown: 'border-beige-150',
                                    }}
                                />
                            )}
                        />
                    )}
                    <form.Subscribe
                        selector={(state) => ({
                            isPristine: state.isPristine,
                            canSubmit: state.canSubmit,
                            isSubmitting: state.isSubmitting,
                            errors: state.errors,
                        })}
                        children={(state) => (
                            <>
                                {state.errors && (
                                    <span className="text-sm text-error">
                                        {state.errors}
                                    </span>
                                )}
                                <Button
                                    type="submit"
                                    fullWidth
                                    disabled={
                                        !state.canSubmit || state.isPristine
                                    }
                                    isLoading={state.isSubmitting}
                                    className={cn(
                                        'btn-primary',
                                        classNames?.submitButton,
                                    )}
                                    loadingClassName="text-inherit">
                                    {dictionary?.formSendButtonTextNewCustomer}
                                </Button>
                            </>
                        )}
                    />
                </div>
            </div>
        </form>
    );
}

export function NewCustomerPopup({
    form,
    onClose,
}: {
    form: Maybe<Form> | undefined;
    onClose?: () => void;
}) {
    const formDictionary = createDictionary(
        form?.dictionaryCollection?.items as KeyValue[],
    );
    return (
        <div className="mx-auto max-w-screen-md bg-black px-6 py-8 text-white md:px-8 md:py-10">
            <NewCustomerForm
                onClose={onClose}
                dictionary={formDictionary}
                classNames={{
                    instagramButton:
                        'border-white group hover:bg-white hover:border-white hover:text-black text-white',
                    submitButton:
                        'btn-inverted hover:bg-black hover:text-white hover:border-white',
                    emailInput: 'placeholder:text-white',
                    selectWrapper: 'bg-black text-white border',
                    selectOption: 'ui-active:bg-gray-100/10',
                    wrapper: 'md:grid-cols-2',
                }}>
                <NewCustomerHeader dictionary={formDictionary} />
            </NewCustomerForm>
        </div>
    );
}
